import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { AUTO_SAVE_INTERVAL_MS } from "@/lib/constants";
import { formatFieldValue, formatFormData, formatDateTimeIST, formatDateIST, formatTimeIST } from "@/lib/formatUtils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, RotateCcw, UserCheck, FileEdit, ClipboardCheck, Scale, AlertTriangle, X, FileBarChart } from "lucide-react";
import DraftSection from "@/components/shared/DraftSection";
import ExportButtons from "@/components/shared/ExportButtons";
import EntryDetailDialog from "@/components/shared/EntryDetailDialog";
import logger from "@/lib/logger";
import HandoverTab from "./security/HandoverTab";
import ShiftSummaryReport from "@/components/shared/ShiftSummaryReport";
import CalibrationForm from "./security/CalibrationForm";
import CourierOtpDialog from "./security/CourierOtpDialog";
import { KeyForm, CourierForm, LogBookForm, SpecialGateForm } from "./security/EntryForms";
import OtherValueDialog from "@/components/shared/OtherValueDialog";

const TABS = {
  key: { api: "/mod6/key", label: "Key Register", draftType: "key", reportType: "key_register" },
  courier: { api: "/mod6/courier", label: "Courier", draftType: "courier", reportType: "courier_register" },
  logbook: { api: "/mod6/logbook", label: "Daily Log Book", draftType: "logbook", reportType: "daily_logbook" },
  special: { api: "/mod6/special", label: "Special Gate", draftType: "special", reportType: "special_gate" },
};

const TAB_COLUMNS = {
  key: [
    { key: "ref_no", label: "Ref No." }, { key: "key_name", label: "Key Name" },
    { key: "issued_to", label: "Issued To" }, { key: "department", label: "Dept" },
    { key: "issued_time", label: "Issued" }, { key: "return_time", label: "Returned" }, { key: "status", label: "Status" },
  ],
  courier: [
    { key: "ref_no", label: "Ref No." }, { key: "direction", label: "Type" },
    { key: "courier_company", label: "Courier Co." },
    { key: "awb_no", label: "AWB No." }, { key: "sender", label: "From" },
    { key: "recipient", label: "To" }, { key: "no_of_packages", label: "Pkgs" }, { key: "status", label: "Status" },
  ],
  logbook: [
    { key: "ref_no", label: "Ref No." }, { key: "observation", label: "Observation" },
    { key: "category", label: "Category" }, { key: "severity", label: "Severity" },
    { key: "shift", label: "Shift" }, { key: "created_by", label: "Recorded By" }, { key: "created_at", label: "Date" },
  ],
  special: [
    { key: "ref_no", label: "Ref No." }, { key: "description", label: "Description" },
    { key: "person_vehicle", label: "Person/Vehicle" }, { key: "authorized_by", label: "Auth. By" },
    { key: "remarks", label: "Remarks" }, { key: "created_at", label: "Date" },
  ],
};

const CALIBRATION_EXPORT_COLUMNS = [
  { key: "ref_no", label: "Ref No." }, { key: "calibration_date", label: "Date" },
  { key: "coil_count", label: "Coils" }, { key: "total_tag_weight", label: "Tag Wt" },
  { key: "total_sil_weight", label: "SIL Wt" }, { key: "total_variation", label: "Var" },
  { key: "total_variation_pct", label: "Var %" },
];

export default function SecurityLogs() {
  const [tab, setTab] = useState("key");
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [detailEntry, setDetailEntry] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [keys, setKeys] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [otherField, setOtherField] = useState(null);
  const [otherValue, setOtherValue] = useState("");
  const [courierOtpId, setCourierOtpId] = useState(null);
  const [calEntries, setCalEntries] = useState([]);
  const [calTotal, setCalTotal] = useState(0);
  const [showCalForm, setShowCalForm] = useState(false);
  const [calForm, setCalForm] = useState({});
  const [calSaving, setCalSaving] = useState(false);
  const [calReminder, setCalReminder] = useState(null);
  const autoSaveRef = useRef(null);
  const { location, user } = useAuth();
  const isManagerOrAdmin = user?.role === "admin" || user?.role === "manager";

  const cfg = TABS[tab] || { api: "", label: "", draftType: "", reportType: "" };

  // `api` and `logger` are stable module imports — intentionally not in dep array.
  const fetchEntries = useCallback(async () => {
    if (tab === "handover" || tab === "calibration" || tab === "shiftsummary" || !cfg.api) return;
    try {
      const { data } = await api.get(cfg.api, { params: { location, search: search || undefined } });
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (err) { logger.error("Failed to fetch entries:", err); }
  }, [tab, location, search, cfg.api]);

  const fetchCalibrations = useCallback(async () => {
    try {
      const { data } = await api.get("/calibration/entries", { params: { location, search: search || undefined } });
      setCalEntries(data.entries || []);
      setCalTotal(data.total || 0);
    } catch (err) { logger.error("Failed to fetch calibrations:", err); }
  }, [location, search]);

  // Check if calibration is due — poll every 5 min
  useEffect(() => {
    const checkDue = async () => {
      try {
        const { data } = await api.get("/calibration/due", { params: { location } });
        setCalReminder(data.is_due ? data : null);
      } catch { setCalReminder(null); }
    };
    checkDue();
    const interval = setInterval(checkDue, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [location]);

  const fetchDrafts = useCallback(async () => {
    if (tab === "handover" || !cfg.draftType) return;
    try {
      const { data } = await api.get(`/drafts/${cfg.draftType}`, { params: { location } });
      setDrafts(data.drafts || []);
    } catch (err) { logger.error("Failed to fetch drafts:", err); }
  }, [tab, location, cfg.draftType]);

  useEffect(() => { fetchEntries(); fetchDrafts(); if (tab === "calibration") fetchCalibrations(); }, [fetchEntries, fetchDrafts, tab, fetchCalibrations]);

  const loadMasters = useCallback(async () => {
    try {
      const [k, c] = await Promise.all([api.get("/masters/keys"), api.get("/masters/courier_companies")]);
      setKeys(k.data.items);
      setCouriers(c.data.items);
    } catch (err) { logger.error("Failed to load masters:", err); }
  }, []);

  useEffect(() => { loadMasters(); }, [loadMasters]);

  const formRef = useRef(form);
  formRef.current = form;
  const draftIdRef = useRef(currentDraftId);
  draftIdRef.current = currentDraftId;

  useEffect(() => {
    if (!showForm || tab === "handover" || !cfg.draftType) return;
    autoSaveRef.current = setInterval(async () => {
      const f = formRef.current;
      if (!f || Object.keys(f).length === 0) return;
      try {
        const payload = { ...f, location, _draft_id: draftIdRef.current || undefined };
        const { data } = await api.post(`/drafts/${cfg.draftType}`, payload);
        if (!draftIdRef.current && data.id) setCurrentDraftId(data.id);
        setLastAutoSave(new Date());
      } catch (err) { logger.error("Auto-save failed:", err); }
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(autoSaveRef.current);
  }, [showForm, tab, location, cfg.draftType]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: formatFieldValue(k, v) }));

  const closeForm = () => {
    setShowForm(false);
    if (currentDraftId && cfg.draftType) api.post(`/drafts/${cfg.draftType}/${currentDraftId}/release-lock`).catch(() => {});
    setCurrentDraftId(null);
    setLastAutoSave(null);
    setForm({});
  };

  const handleSaveDraft = async () => {
    if (tab === "handover" || !cfg.draftType) return;
    try {
      const payload = { ...form, location, _draft_id: currentDraftId || undefined };
      const { data } = await api.post(`/drafts/${cfg.draftType}`, payload);
      if (!currentDraftId && data.id) setCurrentDraftId(data.id);
      toast.success("Draft saved");
      setLastAutoSave(new Date());
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleSubmit = async () => {
    if (tab === "handover" || !cfg.api) return;
    if (tab === "key" && !form.key_name?.trim()) { toast.error("Key Name is required"); return; }
    if (tab === "key" && !form.issued_to?.trim()) { toast.error("Issued To is required"); return; }
    if (tab === "courier" && !form.courier_company?.trim()) { toast.error("Courier Company is required"); return; }
    if (tab === "courier" && !form.awb_no?.trim()) { toast.error("AWB No. is required"); return; }
    if (tab === "logbook" && !form.observation?.trim()) { toast.error("Observation is required"); return; }
    if (tab === "special" && !form.description?.trim()) { toast.error("Description is required"); return; }
    setSaving(true);
    try {
      await api.post(cfg.api, { ...formatFormData(form), location });
      toast.success("Entry created");
      if (currentDraftId) await api.post(`/drafts/${cfg.draftType}/${currentDraftId}/submit`).catch(() => {});
      closeForm();
      fetchEntries();
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const handleReturnKey = async (id) => {
    try { await api.put(`/mod6/key/${id}/return`); toast.success("Key returned"); fetchEntries(); } catch { toast.error("Failed"); }
  };

  const handleOpenDraft = async (draft) => {
    if (tab === "handover" || !cfg.draftType) return;
    try {
      const { data } = await api.get(`/drafts/${cfg.draftType}/${draft.id}`);
      setForm(data.data || {});
      setCurrentDraftId(draft.id);
      setShowForm(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Cannot open draft"); }
  };

  const handleDeleteDraft = async (draftId) => {
    if (tab === "handover" || !cfg.draftType) return;
    try { await api.delete(`/drafts/${cfg.draftType}/${draftId}`); toast.success("Draft deleted"); fetchDrafts(); } catch { toast.error("Failed"); }
  };

  const handleSelectOrOther = (fieldKey, val, masterType) => {
    if (val === "__other__") { setOtherField({ masterType, fieldKey }); return; }
    setField(fieldKey, val);
  };

  const fetchMasters = () => {
    Promise.all([api.get("/masters/keys"), api.get("/masters/courier_companies")]).then(([k, c]) => {
      setKeys(k.data.items); setCouriers(c.data.items);
    }).catch((err) => { logger.error("Failed to refresh masters:", err); });
  };

  const handleOtherSubmit = async () => {
    if (!otherValue.trim() || !otherField) return;
    const val = formatFieldValue(otherField.fieldKey, otherValue.trim());
    setField(otherField.fieldKey, val);
    try { await api.post(`/masters/${otherField.masterType}`, { name: val }); } catch (err) { logger.error("Failed to save master:", err); }
    fetchMasters();
    toast.success(`"${val}" added`);
    setOtherField(null); setOtherValue("");
  };

  const handleCalSubmit = async () => {
    const coils = calForm.coils || [];
    if (coils.length === 0 || !coils[0].tag_weight) {
      toast.error("Add at least one coil with Tag Weight");
      return;
    }
    setCalSaving(true);
    try {
      await api.post("/calibration/entry", { ...calForm, location });
      toast.success("Calibration entry saved");
      setShowCalForm(false);
      setCalForm({});
      fetchCalibrations();
      // Clear reminder since calibration is now done
      setCalReminder(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save calibration");
    }
    setCalSaving(false);
  };

  return (
    <div className="space-y-5" data-testid="security-logs-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Chivo" }}>Security & Logs</h1>
          <p className="text-xs sm:text-sm text-gray-500">MOD-6 · Key Register, Courier, Daily Log, Special Gate, Calibration</p>
        </div>
        <Button onClick={() => { setForm({}); setCurrentDraftId(null); setShowForm(true); }} className="bg-[#1B3C87] hover:bg-[#2650A8] h-10 sm:h-11 text-xs sm:text-sm w-full sm:w-auto" data-testid="new-entry-btn" style={{ display: tab === "handover" || tab === "calibration" || tab === "shiftsummary" ? "none" : undefined }}>
          <Plus size={18} className="mr-1" /> New Entry
        </Button>
      </div>

      <Tabs value={tab} onValueChange={v => { setTab(v); setSearch(""); }}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between flex-wrap gap-2">
          <TabsList className="h-10 sm:h-11 bg-[#1B3C87]/5 flex-wrap">
            {Object.entries(TABS).map(([k, v]) => (
              <TabsTrigger key={k} value={k} className="h-8 sm:h-9 px-3 sm:px-4 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid={`tab-${k}`}>
                {v.label}
              </TabsTrigger>
            ))}
            <TabsTrigger value="handover" className="h-8 sm:h-9 px-3 sm:px-4 text-[11px] sm:text-xs data-[state=active]:bg-emerald-700 data-[state=active]:text-white" data-testid="tab-handover">
              <ClipboardCheck size={14} className="mr-1 hidden sm:block" /> Shift Handover
            </TabsTrigger>
            {isManagerOrAdmin && (
              <TabsTrigger value="shiftsummary" className="h-8 sm:h-9 px-3 sm:px-4 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid="tab-shiftsummary">
                <FileBarChart size={14} className="mr-1 hidden sm:block" /> Shift Summary
              </TabsTrigger>
            )}
            <TabsTrigger value="calibration" className="h-8 sm:h-9 px-3 sm:px-4 text-[11px] sm:text-xs data-[state=active]:bg-violet-700 data-[state=active]:text-white" data-testid="tab-calibration">
              <Scale size={14} className="mr-1 hidden sm:block" /> Calibration
            </TabsTrigger>
          </TabsList>
          <div className="relative w-full sm:w-auto">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 w-full sm:w-52" data-testid="search-input" />
          </div>
        </div>

        <div className="flex items-center justify-between mt-3">
          {tab !== "handover" && tab !== "calibration" && tab !== "shiftsummary" && cfg.reportType && (
            <ExportButtons reportType={cfg.reportType} title={cfg.label} entries={entries} columns={TAB_COLUMNS[tab]} location={location} />
          )}
        </div>

        {tab !== "handover" && tab !== "calibration" && cfg.draftType && (
          <DraftSection drafts={drafts} onOpen={handleOpenDraft} onDelete={handleDeleteDraft} draftType={cfg.draftType} />
        )}

        {/* Key Register */}
        <TabsContent value="key">
          <Card className="mt-3">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
                  {["Ref No.", "Key Name", "Issued To", "Department", "Issued Time", "Return Time", "Status", "Actions"].map(h => (
                    <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400">No entries</TableCell></TableRow>}
                {entries.map(e => (
                  <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                    <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                    <TableCell className="font-medium">{e.key_name || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.issued_to || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.department || "\u2014"}</TableCell>
                    <TableCell className="text-xs">{e.issued_time ? formatDateTimeIST(e.issued_time) : "\u2014"}</TableCell>
                    <TableCell className="text-xs">{e.return_time ? formatDateTimeIST(e.return_time) : "\u2014"}</TableCell>
                    <TableCell><span className={`status-${e.status} px-2 py-0.5 rounded text-xs font-semibold`}>{e.status}</span></TableCell>
                    <TableCell>
                      {e.status === "issued" && (
                        <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={(ev) => { ev.stopPropagation(); handleReturnKey(e.id); }} data-testid={`return-key-${e.id}`}>
                          <RotateCcw size={12} className="mr-1" /> Return
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
          </Card>
        </TabsContent>

        {/* Courier */}
        <TabsContent value="courier">
          <Card className="mt-3">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
                  {["Ref No.", "Type", "Courier Co.", "AWB No.", "From", "To", "Packages", "Status", "Actions"].map(h => (
                    <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-400">No entries</TableCell></TableRow>}
                {entries.map(e => (
                  <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                    <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${e.direction === "outgoing" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                        {e.direction === "outgoing" ? "Out" : "In"}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{e.courier_company || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.awb_no || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.sender || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.recipient || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.no_of_packages || "\u2014"}</TableCell>
                    <TableCell><span className={`status-${e.status} px-2 py-0.5 rounded text-xs font-semibold`}>{e.status?.replace("_", " ")}</span></TableCell>
                    <TableCell>
                      {e.direction !== "outgoing" && e.status === "received" && (
                        <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={(ev) => { ev.stopPropagation(); setCourierOtpId(e.id); }} data-testid={`handover-${e.id}`}>
                          <UserCheck size={12} className="mr-1" /> Handover
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
          </Card>
        </TabsContent>

        {/* Log Book */}
        <TabsContent value="logbook">
          <Card className="mt-3">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
                  {["Ref No.", "Observation", "Category", "Severity", "Shift", "Recorded By", "Date"].map(h => (
                    <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400">No entries</TableCell></TableRow>}
                {entries.map(e => (
                  <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                    <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{e.observation || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.category || "\u2014"}</TableCell>
                    <TableCell><span className={`px-2 py-0.5 rounded text-xs font-semibold ${{"high": "bg-red-100 text-red-700", "medium": "bg-amber-100 text-amber-700"}[e.severity] || "bg-gray-100 text-gray-600"}`}>{e.severity || "low"}</span></TableCell>
                    <TableCell className="text-sm">{e.shift || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.created_by || "\u2014"}</TableCell>
                    <TableCell className="text-xs">{e.created_at ? formatDateIST(e.created_at) : "\u2014"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
          </Card>
        </TabsContent>

        {/* Special Gate */}
        <TabsContent value="special">
          <Card className="mt-3">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
                  {["Ref No.", "Description", "Person/Vehicle", "Authorized By", "Remarks", "Date"].map(h => (
                    <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">No entries</TableCell></TableRow>}
                {entries.map(e => (
                  <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                    <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                    <TableCell className="text-sm">{e.description || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.person_vehicle || "\u2014"}</TableCell>
                    <TableCell className="text-sm">{e.authorized_by || "\u2014"}</TableCell>
                    <TableCell className="text-sm max-w-[150px] truncate">{e.remarks || "\u2014"}</TableCell>
                    <TableCell className="text-xs">{e.created_at ? formatDateIST(e.created_at) : "\u2014"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
          </Card>
        </TabsContent>

        {/* Shift Handover Tab */}
        <TabsContent value="handover">
          <HandoverTab location={location} />
        </TabsContent>

        {/* Shift Summary Tab (manager / admin) */}
        {isManagerOrAdmin && (
          <TabsContent value="shiftsummary">
            <ShiftSummaryReport />
          </TabsContent>
        )}

        {/* Calibration Tab */}
        <TabsContent value="calibration">
          <div className="flex items-center justify-between mt-3 mb-2">
            <ExportButtons reportType="weighbridge_calibration" title="Weighbridge Calibration" entries={calEntries} columns={CALIBRATION_EXPORT_COLUMNS} location={location} />
            <Button onClick={() => { setCalForm({ calibration_date: new Date().toISOString().slice(0, 10), coils: [{ coil_no: "", tag_weight: "", sil_weight: "", tag_photo_url: "", slip_photo_url: "" }] }); setShowCalForm(true); }} className="bg-violet-700 hover:bg-violet-800 h-10 text-xs" data-testid="new-calibration-btn">
              <Scale size={16} className="mr-1" /> New Calibration
            </Button>
          </div>
          <Card className="mt-2">
            <Table>
              <TableHeader>
                <TableRow className="bg-violet-700 hover:bg-violet-700">
                  {["Ref No.", "Date", "Coils", "Total Tag Wt (Kg)", "Total SIL Wt (Kg)", "Variation (Kg)", "Variation %", "Calibrated By"].map(h => (
                    <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {calEntries.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400">No calibration records</TableCell></TableRow>}
                {calEntries.map(e => (
                  <TableRow key={e.id} className="hover:bg-violet-50/50 cursor-pointer" onClick={() => setDetailEntry(e)} data-testid={`cal-row-${e.id}`}>
                    <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                    <TableCell className="text-sm">{e.calibration_date ? formatDateIST(e.calibration_date) : "\u2014"}</TableCell>
                    <TableCell className="text-sm text-center">{e.coil_count || 0}</TableCell>
                    <TableCell className="text-sm font-medium">{e.total_tag_weight ?? "\u2014"}</TableCell>
                    <TableCell className="text-sm font-medium">{e.total_sil_weight ?? "\u2014"}</TableCell>
                    <TableCell className={`text-sm font-semibold ${parseFloat(e.total_variation) === 0 ? "text-emerald-700" : "text-amber-700"}`}>{e.total_variation ?? "\u2014"}</TableCell>
                    <TableCell className={`text-sm font-semibold ${parseFloat(e.total_variation_pct) === 0 ? "text-emerald-700" : "text-amber-700"}`}>{e.total_variation_pct != null ? `${e.total_variation_pct}%` : "\u2014"}</TableCell>
                    <TableCell className="text-xs">{e.created_by || "\u2014"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-3 border-t text-xs text-gray-500">{calTotal} total calibration records</div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Entry Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) closeForm(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle style={{ fontFamily: "Chivo" }}>
                {currentDraftId ? <span className="flex items-center gap-2"><FileEdit size={16} className="text-amber-600" /> Continue Draft</span> : `New ${cfg.label || "Security"} Entry`}
              </DialogTitle>
              {lastAutoSave && <span className="text-[10px] text-gray-400">Draft saved {formatTimeIST(lastAutoSave)}</span>}
            </div>
          </DialogHeader>
          {tab === "key" && <KeyForm form={form} setField={setField} keys={keys} onSelectOrOther={handleSelectOrOther} />}
          {tab === "courier" && <CourierForm form={form} setField={setField} couriers={couriers} onSelectOrOther={handleSelectOrOther} />}
          {tab === "logbook" && <LogBookForm form={form} setField={setField} />}
          {tab === "special" && <SpecialGateForm form={form} setField={setField} />}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={closeForm} className="h-11">Cancel</Button>
            <Button variant="outline" onClick={handleSaveDraft} className="h-11 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="save-draft-btn">
              <FileEdit size={14} className="mr-1" /> Save Draft
            </Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1B3C87] hover:bg-[#2650A8] h-11" data-testid="save-entry-btn">
              {saving ? "Saving..." : "Save Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Other" value dialog */}
      <OtherValueDialog
        otherField={otherField}
        otherValue={otherValue}
        setOtherValue={setOtherValue}
        onSubmit={handleOtherSubmit}
        onClose={() => { setOtherField(null); setOtherValue(""); }}
      />

      {/* Courier OTP Handover Dialog */}
      <CourierOtpDialog
        courierId={courierOtpId}
        onClose={() => setCourierOtpId(null)}
        onHandoverComplete={() => { setCourierOtpId(null); fetchEntries(); }}
      />

      <EntryDetailDialog open={!!detailEntry} onClose={() => setDetailEntry(null)} entry={detailEntry} title="Entry Details" collection={{"keys": "key_register", "courier": "courier_register", "logbook": "logbook_entries"}[tab] || "special_gate"} onRefresh={fetchEntries} />

      {/* Calibration Form Dialog */}
      <Dialog open={showCalForm} onOpenChange={(open) => { if (!open) { setShowCalForm(false); setCalForm({}); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Chivo" }}>
              <span className="flex items-center gap-2"><Scale size={18} className="text-violet-700" /> Weighbridge Calibration Entry</span>
            </DialogTitle>
          </DialogHeader>
          <CalibrationForm form={calForm} setField={(k, v) => setCalForm(prev => ({ ...prev, [k]: v }))} />
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setShowCalForm(false); setCalForm({}); }} className="h-11">Cancel</Button>
            <Button onClick={handleCalSubmit} disabled={calSaving} className="bg-violet-700 hover:bg-violet-800 h-11" data-testid="save-calibration-btn">
              {calSaving ? "Saving..." : "Save Calibration"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calibration Reminder Popup */}
      {calReminder && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" data-testid="calibration-reminder-overlay">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-[90vw] border-2 border-amber-400 animate-in fade-in zoom-in-95">
            <div className="flex items-start gap-3">
              <div className="bg-amber-100 rounded-full p-2.5 shrink-0">
                <AlertTriangle className="text-amber-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: "Chivo" }}>Calibration Overdue</h3>
                <p className="text-sm text-gray-600 mt-1">{calReminder.reason}</p>
                <p className="text-xs text-gray-400 mt-1">Current time: {calReminder.time}</p>
                <p className="text-xs text-amber-600 font-semibold mt-2">This reminder will repeat every 5 minutes until calibration is completed.</p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button variant="outline" className="flex-1 h-10" onClick={() => setCalReminder(null)} data-testid="reminder-dismiss-btn">
                Dismiss
              </Button>
              <Button className="flex-1 h-10 bg-violet-700 hover:bg-violet-800" onClick={() => { setCalReminder(null); setTab("calibration"); setCalForm({ calibration_date: new Date().toISOString().slice(0, 10), coils: [{ coil_no: "", tag_weight: "", sil_weight: "", tag_photo_url: "", slip_photo_url: "" }] }); setShowCalForm(true); }} data-testid="reminder-calibrate-btn">
                <Scale size={16} className="mr-1" /> Calibrate Now
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
